import { Component, Input, OnInit, inject, Inject, NgZone } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MemberDashboardService } from '../member-dashboard.service';

@Component({
  selector: 'app-mood-checkin',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule, MatProgressSpinnerModule],
  templateUrl: './mood-checkin.component.html',
  styleUrl: './mood-checkin.component.css'
})
export class MoodCheckinComponent implements OnInit {
  @Input() memberId: any;
  readonly dialog = inject(MatDialog);
  // Calendar properties
  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();
  calendarDays: any[] = [];
  months: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  years: number[] = [];
  moodCheckinData: any = {};
  moodCheckinResponse: any = null;
  energyDataResponse: any = null;
  isLoading: boolean = false;

  // Emoji image paths mapping (matches API `feeling` values; keys matched case-insensitively)
  feelingEmojiMap: { [key: string]: string } = {
    Happy: '/assets/img/memberdashboard/Happy.png',
    Bored: '/assets/img/memberdashboard/Bored.png',
    Sad: '/assets/img/memberdashboard/Sad.png',
    Unsure: '/assets/img/memberdashboard/Unsure.png',
    Stressed: '/assets/img/memberdashboard/Stressed.png',
    Stress: '/assets/img/memberdashboard/Stressed.png',
    Anxious: '/assets/img/memberdashboard/Anxious.png',
    Optimistic: '/assets/img/memberdashboard/Optimistic.png',
    Exhausted: '/assets/img/memberdashboard/Exhausted.png',
    Excited: '/assets/img/memberdashboard/Excited.png',
    Peaceful: '/assets/img/memberdashboard/Peaceful.png',
    Relaxed: '/assets/img/memberdashboard/Relaxed.png',
    Unsettled: '/assets/img/memberdashboard/Unsettled.png'
  };

  constructor(private dashboard: MemberDashboardService) {
    // Initialize years array (current year - 2 to current year only - no future years)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  ngOnInit(): void {
    // Ensure selected month/year is not in the future
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If selected year is in the future, reset to current year
    if (this.selectedYear > currentYear) {
      this.selectedYear = currentYear;
    }
    
    // If selected year is current year and selected month is in the future, reset to current month
    if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
      this.selectedMonth = currentMonth;
    }
    
    this.loadMoodCheckinData();
  }

  getMonthString(): string {
    // Format month as YYYY-MM (e.g., 2025-09)
    const month = (this.selectedMonth + 1).toString().padStart(2, '0');
    return `${this.selectedYear}-${month}`;
  }

  loadMoodCheckinData(): void {
    if (!this.memberId) {
      this.generateCalendar();
      return;
    }

    this.isLoading = true;
    const monthString = this.getMonthString();

    // Clear previous data
    this.moodCheckinData = {};

    // Call both APIs
    let moodCheckinLoaded = false;
    let energyDataLoaded = false;

    const checkAndGenerateCalendar = () => {
      if (moodCheckinLoaded && energyDataLoaded) {
        this.generateCalendar();
        this.isLoading = false;
      }
    };

    this.dashboard.getMemberMoodCheckIn(this.memberId, monthString).subscribe({
      next: (response) => {
        this.moodCheckinResponse = response;
        this.processMoodCheckinData(response);
        moodCheckinLoaded = true;
        checkAndGenerateCalendar();
      },
      error: (error) => {
        console.error('Error fetching mood checkin data:', error);
        moodCheckinLoaded = true;
        checkAndGenerateCalendar();
      }
    });

    this.dashboard.getEnergyData(this.memberId, monthString).subscribe({
      next: (response) => {
        this.energyDataResponse = response;
        this.processEnergyData(response);
        energyDataLoaded = true;
        checkAndGenerateCalendar();
      },
      error: (error) => {
        console.error('Error fetching energy data:', error);
        energyDataLoaded = true;
        checkAndGenerateCalendar();
      }
    });
  }

  processMoodCheckinData(data: any): void {
    // Process mood checkin data and update moodCheckinData object
    // The API response contains a "feeling" field that maps to emoji images
    if (data && data.result) {
      const result = Array.isArray(data.result) ? data.result : [data.result];
      result.forEach((item: any) => {
        let dateKey: string | null = null;
        
        // Check dateKey field first
        if (item.dateKey) {
          dateKey = item.dateKey;
        } else if (item.date) {
          // Normalize date to avoid timezone issues
          const itemDate = new Date(item.date);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          const itemDay = itemDate.getDate();
          dateKey = `${itemYear}-${itemMonth.toString().padStart(2, '0')}-${itemDay.toString().padStart(2, '0')}`;
        }
        
        if (dateKey) {
          if (!this.moodCheckinData[dateKey]) {
            this.moodCheckinData[dateKey] = { emojis: [], entries: 0 };
          }
          
          // Process feeling field and map to emoji image
          if (item.feeling) {
            const feeling = item.feeling.trim();
            // Case-insensitive matching for feeling values
            const feelingKey = Object.keys(this.feelingEmojiMap).find(
              key => key.toLowerCase() === feeling.toLowerCase()
            );
            if (feelingKey) {
              const emojiPath = this.feelingEmojiMap[feelingKey];
              // Add emoji to the array if it doesn't already exist
              if (!this.moodCheckinData[dateKey].emojis.includes(emojiPath)) {
                this.moodCheckinData[dateKey].emojis.push(emojiPath);
              }
            }
          }
          
          this.moodCheckinData[dateKey].entries = (this.moodCheckinData[dateKey].entries || 0) + 1;
        }
      });
    }
  }

  processEnergyData(data: any): void {
    // Process energy data and update moodCheckinData object
    // This will depend on the API response structure
    if (data && data.result) {
      const result = Array.isArray(data.result) ? data.result : [data.result];
      result.forEach((item: any) => {
        let dateKey: string | null = null;
        
        // Check dateKey field first
        if (item.dateKey) {
          dateKey = item.dateKey;
        } else if (item.date) {
          // Normalize date to avoid timezone issues
          const itemDate = new Date(item.date);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          const itemDay = itemDate.getDate();
          dateKey = `${itemYear}-${itemMonth.toString().padStart(2, '0')}-${itemDay.toString().padStart(2, '0')}`;
        }
        
        if (dateKey) {
          if (!this.moodCheckinData[dateKey]) {
            this.moodCheckinData[dateKey] = { emojis: [], entries: 0 };
          }
          // Update entries count
          this.moodCheckinData[dateKey].entries = (this.moodCheckinData[dateKey].entries || 0) + 1;
        }
      });
    }
  }

  generateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.selectedYear, this.selectedMonth, 1);
    const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.selectedYear, this.selectedMonth, day);
      // Format dateKey manually to ensure exact format YYYY-MM-DD (matching API format)
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = date.getDate().toString().padStart(2, '0');
      const dateKey = `${year}-${month}-${dayStr}`;
      const dayData = this.moodCheckinData[dateKey] || { emojis: [], entries: 0 };
      
      this.calendarDays.push({
        day: day,
        date: date,
        dateKey: dateKey,
        isCurrentMonth: true,
        emojis: dayData.emojis || [],
        entries: dayData.entries || 0
      });
    }

    // Add empty cells at the end to complete the grid (fill to 7 columns)
    const totalCells = this.calendarDays.length;
    const remainingCells = 7 - (totalCells % 7);
    if (remainingCells < 7) {
      for (let i = 0; i < remainingCells; i++) {
        this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
      }
    }
  }

  onMonthYearChange(): void {
    // Safety check: ensure selected month/year is not in the future
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If selected year is in the future, reset to current year
    if (this.selectedYear > currentYear) {
      this.selectedYear = currentYear;
    }
    
    // If selected year is current year and selected month is in the future, reset to current month
    if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
      this.selectedMonth = currentMonth;
    }
    
    // Reload data for the new month/year
    this.loadMoodCheckinData();
  }

  isMonthDisabled(monthIndex: number): boolean {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If selected year is current year, disable future months
    if (this.selectedYear === currentYear) {
      return monthIndex > currentMonth;
    }
    
    // If selected year is in the past, all months are available
    return false;
  }

  isYearDisabled(year: number): boolean {
    const currentYear = new Date().getFullYear();
    // Disable future years
    return year > currentYear;
  }

  getDayName(dayIndex: number): string {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THUR', 'FRI', 'SAT'];
    return days[dayIndex];
  }

  onDayClick(day: any): void {
    if (day && day.isCurrentMonth && day.entries > 0) {
      console.log('Clicked day dateKey:', day.dateKey);
      console.log('Mood checkin response:', this.moodCheckinResponse);
      console.log('Energy data response:', this.energyDataResponse);
      
      // Get filtered data for this date
      const moodCheckins = this.getMoodCheckinsForDate(day.dateKey);
      const energyLevels = this.getEnergyLevelsForDate(day.dateKey);
      
      console.log('Filtered moodCheckins:', moodCheckins);
      console.log('Filtered energyLevels:', energyLevels);
      
      // Format date for display
      const dateObj = new Date(day.date);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[dateObj.getDay()];
      const formattedDate = formatDate(dateObj, 'MMMM d, y', 'en');
      
      // Open dialog
      this.dialog.open(MoodCheckinDetailDialogComponent, {
        width: '92%',
        maxWidth: '1350px',
        hasBackdrop: true,
        backdropClass: 'mood-checkin-dialog-backdrop',
        disableClose: false,
        data: {
          date: formattedDate,
          dayName: dayName,
          moodCheckins: moodCheckins,
          energyLevels: energyLevels,
          feelingEmojiMap: this.feelingEmojiMap
        }
      });
    }
  }

  getMoodCheckinsForDate(dateKey: string): any[] {
    if (!this.moodCheckinResponse || !this.moodCheckinResponse.result) {
      return [];
    }
    const result = Array.isArray(this.moodCheckinResponse.result) 
      ? this.moodCheckinResponse.result 
      : [this.moodCheckinResponse.result];
    
    return result.filter((item: any) => {
      // Check dateKey field first (exact string match)
      if (item.dateKey) {
        const matches = item.dateKey === dateKey;
        if (!matches) {
          console.log(`dateKey mismatch: API="${item.dateKey}" vs Calendar="${dateKey}"`);
        }
        return matches;
      }
      
      // Check date field and normalize to avoid timezone issues
      if (item.date) {
        try {
          const itemDate = new Date(item.date);
          // Normalize to local date (ignore time component)
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          const itemDay = itemDate.getDate();
          const itemDateKey = `${itemYear}-${itemMonth.toString().padStart(2, '0')}-${itemDay.toString().padStart(2, '0')}`;
          
          return itemDateKey === dateKey;
        } catch (e) {
          console.error('Error parsing date:', item.date, e);
          return false;
        }
      }
      return false;
    });
  }

  getEnergyLevelsForDate(dateKey: string): any[] {
    if (!this.energyDataResponse || !this.energyDataResponse.result) {
      return [];
    }
    const result = Array.isArray(this.energyDataResponse.result) 
      ? this.energyDataResponse.result 
      : [this.energyDataResponse.result];
    
    return result.filter((item: any) => {
      // Check dateKey field first (exact string match)
      if (item.dateKey) {
        return item.dateKey === dateKey;
      }
      
      // Check date field and normalize to avoid timezone issues
      if (item.date) {
        try {
          const itemDate = new Date(item.date);
          // Normalize to local date (ignore time component)
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          const itemDay = itemDate.getDate();
          const itemDateKey = `${itemYear}-${itemMonth.toString().padStart(2, '0')}-${itemDay.toString().padStart(2, '0')}`;
          
          return itemDateKey === dateKey;
        } catch (e) {
          console.error('Error parsing date:', item.date, e);
          return false;
        }
      }
      return false;
    });
  }
}

// Dialog Component
@Component({
  selector: 'app-mood-checkin-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './mood-checkin-detail-dialog.component.html',
  styleUrl: './mood-checkin-detail-dialog.component.css'
})
export class MoodCheckinDetailDialogComponent {
  selectedImage: string | null = null;
  playingAudios: { [key: string]: boolean } = {};
  audioElements: { [key: string]: HTMLAudioElement } = {};
  audioDurations: { [key: string]: string } = {};
  audioCurrentTimes: { [key: string]: number } = {};

  energyLevelMap: { [key: number]: { title: string; subtitle: string } } = {
    1: { title: 'Low Energy', subtitle: 'Tired / Drained / Foggy' },
    2: { title: 'Medium Energy', subtitle: 'Okay / Uneven / Manageable' },
    3: { title: 'High Energy', subtitle: 'Strong / Steady / Ready' },
    5: { title: 'Productive & Motivated', subtitle: 'High Energy' },
    4: { title: 'Engaged & Energised (In Control)', subtitle: 'Optimal Energy' }
  };

  energyLevelIconMap: { [key: number]: string } = {
    1: '/assets/img/memberdashboard/Low Energy.png',
    2: '/assets/img/memberdashboard/Medium Energy.png',
    3: '/assets/img/memberdashboard/High Energy.png',
    5: '/assets/img/memberdashboard/Relaxed.png',
    4: '/assets/img/memberdashboard/Happy.png'
  };

  getEnergyLevelIcon(level: number): string {
    return this.energyLevelIconMap[level] || '/assets/img/memberdashboard/Medium Energy.png';
  }

  constructor(
    public dialogRef: MatDialogRef<MoodCheckinDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private ngZone: NgZone
  ) {
    // Preload audio metadata for all mood checkins
    if (this.data.moodCheckins) {
      this.data.moodCheckins.forEach((checkin: any) => {
        if (checkin.voiceUrl && checkin.id) {
          this.preloadAudio(checkin.voiceUrl, checkin.id);
        }
      });
    }
  }

  preloadAudio(audioUrl: string, id: string): void {
    const audio = new Audio(audioUrl);
    audio.preload = 'metadata';
    audio.loop = false;
    this.audioCurrentTimes[id] = 0; // Initialize current time
    
    audio.addEventListener('loadedmetadata', () => {
      const minutes = Math.floor(audio.duration / 60);
      const seconds = Math.floor(audio.duration % 60);
      this.audioDurations[id] = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });
    audio.addEventListener('error', () => {
      this.audioDurations[id] = '0:00';
      this.audioCurrentTimes[id] = 0;
    });
    
    // Add timeupdate listener to preloaded audio
    audio.addEventListener('timeupdate', () => {
      this.ngZone.run(() => {
        this.audioCurrentTimes[id] = audio.currentTime;
        // Check if reached end
        if (audio.duration && audio.currentTime >= audio.duration) {
          this.playingAudios[id] = false;
          this.audioCurrentTimes[id] = 0;
        }
      });
    });
    
    // Add play, pause, ended listeners
    audio.addEventListener('play', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = true;
      });
    });
    
    audio.addEventListener('pause', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = false;
      });
    });
    
    audio.addEventListener('ended', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = false;
        this.audioCurrentTimes[id] = 0;
        audio.currentTime = 0;
      });
    });
    
    this.audioElements[id] = audio;
  }

  getFeelingEmoji(feeling: string): string {
    if (!feeling || !this.data.feelingEmojiMap) return '';
    const feelingKey = Object.keys(this.data.feelingEmojiMap).find(
      key => key.toLowerCase() === feeling.trim().toLowerCase()
    );
    return feelingKey ? this.data.feelingEmojiMap[feelingKey] : '';
  }

  formatTime(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return formatDate(date, 'h:mm a', 'en');
  }

  toggleAudio(audioUrl: string, id: string): void {
    // Use preloaded audio if available, otherwise create new one
    if (!this.audioElements[id]) {
      const audio = new Audio(audioUrl);
      audio.loop = false;
      this.audioCurrentTimes[id] = 0;
      
      audio.addEventListener('loadedmetadata', () => {
        const minutes = Math.floor(audio.duration / 60);
        const seconds = Math.floor(audio.duration % 60);
        this.audioDurations[id] = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        this.audioCurrentTimes[id] = 0;
      });
      
      audio.addEventListener('play', () => {
        this.ngZone.run(() => {
          this.playingAudios[id] = true;
        });
      });
      
      audio.addEventListener('pause', () => {
        this.ngZone.run(() => {
          this.playingAudios[id] = false;
        });
      });
      
      audio.addEventListener('ended', () => {
        this.ngZone.run(() => {
          this.playingAudios[id] = false;
          this.audioCurrentTimes[id] = 0;
          audio.currentTime = 0;
        });
      });
      
      audio.addEventListener('timeupdate', () => {
        this.ngZone.run(() => {
          this.audioCurrentTimes[id] = audio.currentTime;
          if (audio.duration && audio.currentTime >= audio.duration) {
            this.playingAudios[id] = false;
            this.audioCurrentTimes[id] = 0;
          }
        });
      });
      
      this.audioElements[id] = audio;
    }

    const audio = this.audioElements[id];
    
    // Ensure current time is initialized
    if (this.audioCurrentTimes[id] === undefined) {
      this.audioCurrentTimes[id] = 0;
    }
    
    // Check if currently playing
    const isCurrentlyPlaying = !audio.paused && !audio.ended;
    
    if (isCurrentlyPlaying) {
      // Currently playing - pause it
      audio.pause();
      this.playingAudios[id] = false;
    } else {
      // Pause all other audios
      Object.keys(this.audioElements).forEach(key => {
        if (key !== id && this.audioElements[key] && !this.audioElements[key].paused) {
          this.audioElements[key].pause();
          this.playingAudios[key] = false;
        }
      });
      
      // If audio ended, reset to beginning
      if (audio.ended) {
        audio.currentTime = 0;
        this.audioCurrentTimes[id] = 0;
      } else {
        // Initialize current time if not set
        this.audioCurrentTimes[id] = audio.currentTime || 0;
      }
      
      // Play the audio
      audio.play().then(() => {
        // Ensure current time starts updating
        this.audioCurrentTimes[id] = audio.currentTime || 0;
      }).catch((error) => {
        console.error('Error playing audio:', error);
        this.playingAudios[id] = false;
      });
    }
  }

  getAudioDuration(audioUrl: string, id: string): string {
    // Get current time from audio element if available, otherwise use stored value
    let currentTime = 0;
    if (this.audioElements[id]) {
      currentTime = this.audioElements[id].currentTime || 0;
      // Update stored value
      this.audioCurrentTimes[id] = currentTime;
    } else {
      currentTime = this.audioCurrentTimes[id] || 0;
    }
    
    // Get total duration in seconds
    let totalSeconds = 0;
    if (this.audioElements[id] && this.audioElements[id].duration && !isNaN(this.audioElements[id].duration)) {
      totalSeconds = this.audioElements[id].duration;
    } else if (this.audioDurations[id] && this.audioDurations[id] !== '0:00') {
      const parts = this.audioDurations[id].split(':');
      if (parts.length === 2) {
        totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }
    
    // Format current time as MM:SS (round up to start from 00:01 when playing)
    const currentSeconds = Math.ceil(currentTime);
    const currentMinutes = Math.floor(currentSeconds / 60);
    const currentSecondsRemainder = currentSeconds % 60;
    const currentTimeFormatted = `${currentMinutes.toString().padStart(2, '0')}:${currentSecondsRemainder.toString().padStart(2, '0')}`;
    
    // Format total duration as MM:SS
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalSecondsRemainder = Math.floor(totalSeconds % 60);
    const totalTimeFormatted = `${totalMinutes.toString().padStart(2, '0')}:${totalSecondsRemainder.toString().padStart(2, '0')}`;
    
    return `${currentTimeFormatted}/${totalTimeFormatted}`;
  }

  isAudioPlaying(id: string): boolean {
    // Check if audio is actually playing (not paused and not ended)
    if (this.audioElements[id]) {
      const audio = this.audioElements[id];
      const isPlaying = !audio.paused && !audio.ended;
      // Sync state
      if (isPlaying !== this.playingAudios[id]) {
        this.playingAudios[id] = isPlaying;
      }
      return isPlaying;
    }
    return false;
  }

  openImageLightbox(imageUrl: string): void {
    this.selectedImage = imageUrl;
  }

  closeImageLightbox(): void {
    this.selectedImage = null;
  }

  closeDialog(): void {
    // Stop all playing audios
    Object.keys(this.audioElements).forEach(key => {
      if (this.playingAudios[key]) {
        this.audioElements[key].pause();
      }
    });
    this.dialogRef.close();
  }
}

